import { useLang } from "@/i18n/LanguageContext";
import { SEO } from "@/components/site/SEO";
import { ServiceCards } from "@/components/site/ServiceCards";
import { ServicesClosing } from "@/components/site/ServicesClosing";
import { KineticText } from "@/components/site/KineticText";
import { AuroraGlow } from "@/components/site/AuroraGlow";
import { motion } from "framer-motion";

// Services — kicker + headline + three color-coded cards (Video / Voz de
// Marca / Conversión), then a clean, brand-only close. No public pricing:
// cost and timeline are scoped per project (see FAQ). Only one CTA on the
// whole page, in the closing block — none inside the cards. The headline
// and subtitle already existed in content but weren't rendered — surfacing
// them (with a moving signal-color aurora behind) gives the page an actual
// opening statement instead of dropping straight into the card grid.
export default function Services() {
  const { t, lang, path } = useLang();
  const s = t.services;

  return (
    <div className="bg-ink text-paper" data-testid="services-page">
      <SEO title={s.meta.title} description={s.meta.description} lang={lang} />

      <section className="relative overflow-hidden pt-32 pb-16 md:pb-20 px-6 md:px-12">
        <AuroraGlow tone="signal" />
        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="block font-mono text-[11px] tracking-[0.3em] uppercase text-accent"
            data-testid="services-kicker"
          >
            {s.kicker}
          </motion.span>
          {s.title && (
            <KineticText
              lines={[s.title]}
              tag="h1"
              testId="services-title"
              className="mt-6 font-heading font-semibold italic tracking-tight leading-[1.1] text-4xl md:text-6xl lg:text-7xl [text-wrap:balance]"
            />
          )}
          {s.subtitle && (
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: 0.5 }}
              className="mt-6 font-body text-lg md:text-xl text-paper/70 max-w-2xl mx-auto leading-relaxed [text-wrap:pretty]"
              data-testid="services-subtitle"
            >
              {s.subtitle}
            </motion.p>
          )}
        </div>
      </section>

      <section className="px-6 md:px-12 pb-24 md:pb-32">
        <div className="max-w-6xl mx-auto">
          <ServiceCards
            chapters={s.chapters}
            forQuienLabel={s.forQuienLabel}
            incluyeLabel={s.incluyeLabel}
          />
        </div>
      </section>

      <ServicesClosing closing={s.closing} path={path} />
    </div>
  );
}
